import {AgEltBoxSelection, IEltBoxSelection} from "back/edit/wed/features/boxSel";
import {EWedletEditMode, EWedletEditModeLabel, IWedlet, WEDLET} from "back/edit/wed/wedlets/wedlet";
import {DOM, JSX} from "lib/commons/xml/dom";
import {IJmlNode} from "lib/commons/xml/jml";
import {IXmlMsg} from "lib/edit/ot/xmlHouse";
import {LANG} from "lib/commons/lang";
import {REG} from "lib/commons/registry";
import {AgEltBoxInputAnnotable, removeAnnots} from "back/edit/wed/wedlets/box/boxTags";
import {DOMSH} from "lib/commons/xml/domsh";
import {Action, IAction} from "lib/commons/actions";
import {POPUP} from "back/commons/widgets/popups";
import {BarActions, OBarActionsInit} from "back/commons/widgets/bars";

/** box-web-frag pour l'affichage du fragment avec les fonctions de copier / coller / effacer. */
class BoxWebFrag extends HTMLElement implements IEltBoxSelection {

	wedlet: IWedlet;

	disabled: boolean;

	xhtmlValue: string;

	previewFrame: HTMLIFrameElement;

	configWedletElt(tpl: Element, wedlet: IWedlet) {
		this.wedlet = wedlet;
		this.disabled = !WEDLET.isWritableWedlet(wedlet);
		const wedMgr = wedlet.wedMgr;
		const reg = wedMgr.reg;
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		WEDLET.installSkins(tpl, sr, wedlet, this.localName);

		sr.appendChild(<BarActions î={{
			reg,
			actionContext: this,
			actions: reg.getList<IAction<BoxWebFrag>>("wed:BoxWebFrag:actions"),
			uiContext: 'dialog',
			disableFullOverlay: true
		} as OBarActionsInit<BoxWebFrag>}/>);
		this.previewFrame = sr.appendChild(<iframe/>) as HTMLIFrameElement;
	}

	setNewFrag(elt: Element) {
		if (!elt) return this.eraseFrag();
		const wedlet = this.wedlet;
		if (!WEDLET.isWritableWedlet(wedlet)) return;
		let xml = "";
		let vNode = elt.firstChild;
		while(vNode) {
			xml += DOM.ser(vNode, false);
			vNode = vNode.nextSibling;
		}
		//console.log("setNewFrag::::", xml);
		if (wedlet.isVirtual()) {
			WEDLET.insertDatasFromDisplay(wedlet, null, xml);
		} else {
			wedlet.wedMgr.docHolder.newBatch().setText(this.wedlet.wedAnchor, xml).doBatch();
		}
	}

	eraseFrag() {
		const wedlet = this.wedlet;
		if (wedlet.isVirtual() || !WEDLET.isWritableWedlet(wedlet)) return;
		wedlet.wedMgr.docHolder.newBatch().deleteSequence(this.wedlet.wedAnchor, 1).doBatch();
	}

	setEditMode(mode: EWedletEditMode) {
		this.disabled = mode !== EWedletEditMode.write;
		DOM.setAttr(this, "edit-mode", EWedletEditModeLabel[mode]);
	}

	refreshBindValue(val: IJmlNode) {
		this.xhtmlValue = val as string;
		if (this.wedlet.isVirtual()) removeAnnots(this);
		if (this.previewFrame.contentDocument) {
			this.updateFrameBody();
		} else {
			this.previewFrame.onload = this.updateFrameBody.bind(this);
		}
	}

	private updateFrameBody() {
		this.previewFrame.onload = null;
		const body = this.previewFrame.contentDocument.body;
		if (!this.xhtmlValue) {
			body.innerHTML = `<div style="font-style: italic; color: gray; text-align: center; margin: 1em; font-family: sans-serif;">Fragment HTML vide</div>`;
		} else {
			const dom = DOM.parseDomValid(this.xhtmlValue);
			if (!dom) {
				body.innerHTML = `<div style="font-style: italic; color:red; text-align: center; margin: 1em; font-family: sans-serif;">Fragment HTML invalide</div>`;
				body.appendChild(<pre>{this.xhtmlValue}</pre>);
			} else {
				body.textContent = null;
				body.appendChild(body.ownerDocument.importNode(dom.documentElement, true));
			}
		}
	}

	insertChars(from: number, chars: string, msg: IXmlMsg) {
		this.refreshBindValue(LANG.stringInsert(this.xhtmlValue, from, chars));
	}

	deleteChars(from: number, len: number, msg: IXmlMsg) {
		this.refreshBindValue(LANG.stringDelete(this.xhtmlValue, from, len));
	}

	replaceChars(chars: string, msg: IXmlMsg) {
		this.refreshBindValue(chars);
	}

	isEmpty(): boolean {return !this.xhtmlValue}
}

AgEltBoxSelection(AgEltBoxInputAnnotable(BoxWebFrag), {selMode: "box"});

REG.reg.registerSkin('box-web-frag', 1, /* language=CSS */ `
	:host {
		display: flex;
		min-height: min-content;
		min-width: 5em;
		flex-direction: column;
		border: 1px solid var(--border-color);
		margin: 0;
	}

	:host(:focus) {
		outline: var(--edit-input-focus);
	}

	c-bar-actions {
		border-bottom: 1px solid var(--border-color);
	}

	iframe {
		width: 100%;
		min-height: 400px;
		border: none;
	}
`);

window.customElements.define("box-web-frag", BoxWebFrag);


class BoxWebFragCopy extends Action<BoxWebFrag> {
	constructor() {
		super('fragCopy');
		this._label = "Copier";
		this._description = "Copier le fragment HTML dans le presse-papier";
	}

	async execute(ctx: BoxWebFrag, ev?: Event) {
		if (!ctx.xhtmlValue) {
			POPUP.showNotifInfo("Ce fragment est vide", ctx);
			return;
		}
		try {
			await navigator.clipboard.writeText(ctx.xhtmlValue);
		} catch (e) {
			POPUP.showNotifForbidden("Copie dans le presse-papier impossible. Vérifiez dans votre navigateur que vous avez autorisé ce site à accéder au presse-papier.", ctx);
		}
	}
}


class BoxWebFragPaste extends Action<BoxWebFrag> {

	constructor() {
		super('fragPaste');
		this._label = "Coller";
		this._description = "Coller le fragment HTML";
	}

	isEnabled(ctx: BoxWebFrag): boolean {
		if (ctx.disabled) return false;
		return super.isEnabled(ctx);
	}

	async execute(ctx: BoxWebFrag, ev?: Event) {
		let ct: string;
		try {
			ct = await navigator.clipboard.readText();
		} catch (e) {
			POPUP.showNotifForbidden("Impossible de lire dans le presse-papier. Vérifiez dans votre navigateur que vous avez autorisé ce site à accéder au presse-papier.", ctx);
			return;
		}

		const doc = ct ? DOM.parseDomValid(`<body>${ct}</body>`, null, "text/html") : null;
		if (!doc) {
			//XXX tenter en xhtml ?
			POPUP.showNotifInfo("Aucun contenu HTML n'a pu être reconnu", ctx);
			return;
		}
		ctx.setNewFrag(doc.body);
	}
}

class BoxWebFragErase extends Action<BoxWebFrag> {
	constructor() {
		super('fragErase');
		this._label = "Effacer";
		this._description = "Effacer le fragment HTML";
	}

	isEnabled(ctx: BoxWebFrag): boolean {
		if (ctx.disabled) return false;
		return super.isEnabled(ctx);
	}

	execute(ctx: BoxWebFrag, ev?: Event) {
		ctx.eraseFrag();
	}
}

function addAction(action: Action<BoxWebFrag>) {
	REG.reg.addToList("wed:BoxWebFrag:actions", action.getId(), 1, action);
}

addAction(new BoxWebFragPaste());
addAction(new BoxWebFragCopy());
addAction(new BoxWebFragErase());
